'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { idbStorage } from '@/lib/idb-storage';

export interface RecentBook {
  bookKey: string;        // "title::fileName" — same key as reading-progress
  title: string;
  authors: string[];
  downloadUrl: string;    // OPDS acquisition link href (FB2)
  libraryId: string;      // to look up LibraryConnection for auth
  fileName: string;       // for openBook()
  lastOpened: number;     // Date.now()
  totalChapters: number;  // spine.length, for progress display
}

const MAX_RECENT = 10;

interface RecentBooksState {
  recentBooks: RecentBook[];
  addRecentBook: (book: RecentBook) => void;
  removeRecentBook: (bookKey: string) => void;
}

export const useRecentBooksStore = create<RecentBooksState>()(
  persist(
    (set) => ({
      recentBooks: [],

      addRecentBook: (book) =>
        set((state) => {
          // Remove existing entry for this book (if any) then prepend
          const filtered = state.recentBooks.filter(
            (b) => b.bookKey !== book.bookKey
          );
          return {
            recentBooks: [book, ...filtered].slice(0, MAX_RECENT),
          };
        }),

      removeRecentBook: (bookKey) =>
        set((state) => ({
          recentBooks: state.recentBooks.filter((b) => b.bookKey !== bookKey),
        })),
    }),
    {
      name: 'bookshelf-recent-books',
      storage: createJSONStorage(() => idbStorage),
      // Bump in lockstep with the library store reset (2026-05-09): the
      // recent-books list is a cache derived from previously-opened
      // libraries, so it should clear when those libraries do. Reading
      // progress (separate store) is intentionally preserved — that's
      // genuine state, not a cache.
      version: 2,
      migrate: () => ({ recentBooks: [] }),
    }
  )
);
